import { colorsTuple, virtualColor } from "@mantine/core"

/**
 * Never import it!
 * Prefer using `const { colors } = useMantineTheme()`
 */
const gray = [
  "#F8FAFC",
  "#E2E8F0",
  "#CBD5E1",
  "#94A3B8",
  "#64748B",
  "#475569",
  "#334155",
  "#1E293B",
  "#182235",
  "#0F172A",
  "#020617",
]

export const colors = {
  primary: [
    "#EEF2FF",
    "#E0E7FF",
    "#C7D2FE",
    "#A5B4FC",
    "#818CF8",
    "#6366F1",
    "#4F46E5",
    "#4338CA",
    "#3730A3",
    "#312E81",
    "#1E1B4B",
  ],
  green: [
    "#ECFDF5",
    "#D1FAE5",
    "#A7F3D0",
    "#6EE7B7",
    "#34D399",
    "#10B981",
    "#059669",
    "#047857",
    "#065F46",
    "#064E3B",
    "#022C22",
  ],
  red: [
    "#FEF2F2",
    "#FEE2E2",
    "#FECACA",
    "#FCA5A5",
    "#F87171",
    "#EF4444",
    "#DC2626",
    "#B91C1C",
    "#991B1B",
    "#7F1D1D",
    "#450A0A",
  ],
  gray,
  // Surface hierarchy - Light theme
  lightSurfaceGround: colorsTuple(gray[0]), // Page background
  lightSurfaceCard: colorsTuple("#FFFFFF"), // White cards
  lightSurfaceHover: colorsTuple(gray[1]), // Hover state for menu items (visible on white)
  lightSurfaceSelected: colorsTuple(gray[2]), // Selected state (darker than hover)
  lightSurfaceHeader: colorsTuple(gray[1]), // Table headers, section headers
  lightSurfaceSidebar: colorsTuple(gray[8]), // Sidebar - always dark but lighter in light mode
  lightSurfaceSidebarHover: colorsTuple(gray[5]), // Sidebar hover - lighter than sidebar bg

  // Surface hierarchy - Dark theme
  darkSurfaceGround: colorsTuple(gray[9]), // Page background
  darkSurfaceCard: colorsTuple(gray[8]), // Elevated cards
  darkSurfaceHover: colorsTuple(gray[6]), // Hover state
  darkSurfaceSelected: colorsTuple(gray[6]), // Selected state
  darkSurfaceHeader: colorsTuple(gray[7]), // Table headers, section headers
  darkSurfaceSidebar: colorsTuple(gray[8]), // Sidebar - darker in dark mode
  darkSurfaceSidebarHover: colorsTuple(gray[5]), // Sidebar hover - lighter than sidebar bg

  // Borders - same for both themes for now
  lightBorder: colorsTuple(gray[2]),
  darkBorder: colorsTuple(gray[6]),
  lightBorderSubtle: colorsTuple(gray[1]),
  darkBorderSubtle: colorsTuple(gray[7]),

  // Text hierarchy
  lightTextPrimary: colorsTuple(gray[9]),
  darkTextPrimary: colorsTuple(gray[0]),
  lightTextSecondary: colorsTuple(gray[6]),
  darkTextSecondary: colorsTuple(gray[3]),
  lightTextMuted: colorsTuple(gray[5]),
  darkTextMuted: colorsTuple(gray[4]),
  lightTextDisabled: colorsTuple(gray[4]),
  darkTextDisabled: colorsTuple(gray[3]),

  // Sidebar text colors (always light text on dark background)
  lightTextSidebar: colorsTuple(gray[0]), // Light text for dark sidebar in light mode
  darkTextSidebar: colorsTuple(gray[0]), // Light text for dark sidebar in dark mode
  lightTextSidebarMuted: colorsTuple(gray[2]), // Muted light text for sidebar in light mode
  darkTextSidebarMuted: colorsTuple(gray[2]), // Muted light text for sidebar in dark mode

  // Accent/Status colors
  lightWarning: colorsTuple("#D97706"), // amber-600 equivalent for warnings/bookmarks
  darkWarning: colorsTuple("#FCD34D"), // amber-300 equivalent for dark mode
  lightInfo: colorsTuple("#0891B2"), // cyan-600 equivalent for info/secondary
  darkInfo: colorsTuple("#67E8F9"), // cyan-300 equivalent for dark mode

  // Primary highlight colors (for interactive elements)
  lightPrimaryHighlight: colorsTuple("#4F46E5"), // primary[6]
  darkPrimaryHighlight: colorsTuple("#818CF8"), // primary[4] - lighter for dark mode

  // Error/danger colors for alerts, errors, and destructive actions
  lightError: colorsTuple("#EF4444"), // red[5]
  darkError: colorsTuple("#F87171"), // red[4] - lighter for dark mode

  // Text on error backgrounds (high contrast)
  lightTextOnError: colorsTuple("#FFFFFF"),
  darkTextOnError: colorsTuple("#FFFFFF"),

  // Cost change indicators (for financial data visualization)
  lightCostIncrease: colorsTuple("#DC2626"), // red[6] - cost went up (bad)
  darkCostIncrease: colorsTuple("#F87171"), // red[4] - lighter for dark mode
  lightCostDecrease: colorsTuple("#059669"), // green[6] - cost went down (good)
  darkCostDecrease: colorsTuple("#34D399"), // green[4] - lighter for dark mode

  // Success/positive states
  lightSuccess: colorsTuple("#059669"), // green[6]
  darkSuccess: colorsTuple("#34D399"), // green[4]

  //  TODO: Now using Virtual Colors, we can phase out "dark" in the next PR
  dark: gray,

  // Virtual colors for automatic light/dark mode switching
  // Surface hierarchy
  surfaceGround: virtualColor({
    name: "surfaceGround",
    light: "lightSurfaceGround",
    dark: "darkSurfaceGround",
  }),
  surfaceCard: virtualColor({
    name: "surfaceCard",
    light: "lightSurfaceCard",
    dark: "darkSurfaceCard",
  }),
  surfaceHover: virtualColor({
    name: "surfaceHover",
    light: "lightSurfaceHover",
    dark: "darkSurfaceHover",
  }),
  surfaceSelected: virtualColor({
    name: "surfaceSelected",
    light: "lightSurfaceSelected",
    dark: "darkSurfaceSelected",
  }),
  surfaceHeader: virtualColor({
    name: "surfaceHeader",
    light: "lightSurfaceHeader",
    dark: "darkSurfaceHeader",
  }),
  surfaceSidebar: virtualColor({
    name: "surfaceSidebar",
    light: "lightSurfaceSidebar",
    dark: "darkSurfaceSidebar",
  }),
  surfaceSidebarHover: virtualColor({
    name: "surfaceSidebarHover",
    light: "lightSurfaceSidebarHover",
    dark: "darkSurfaceSidebarHover",
  }),

  // Borders
  border: virtualColor({
    name: "border",
    light: "lightBorder",
    dark: "darkBorder",
  }),
  borderSubtle: virtualColor({
    name: "borderSubtle",
    light: "lightBorderSubtle",
    dark: "darkBorderSubtle",
  }),

  // Text
  textPrimary: virtualColor({
    name: "textPrimary",
    light: "lightTextPrimary",
    dark: "darkTextPrimary",
  }),
  textSecondary: virtualColor({
    name: "textSecondary",
    light: "lightTextSecondary",
    dark: "darkTextSecondary",
  }),
  textMuted: virtualColor({
    name: "textMuted",
    light: "lightTextMuted",
    dark: "darkTextMuted",
  }),
  textDisabled: virtualColor({
    name: "textDisabled",
    light: "lightTextDisabled",
    dark: "darkTextDisabled",
  }),
  textSidebar: virtualColor({
    name: "textSidebar",
    light: "lightTextSidebar",
    dark: "darkTextSidebar",
  }),
  textSidebarMuted: virtualColor({
    name: "textSidebarMuted",
    light: "lightTextSidebarMuted",
    dark: "darkTextSidebarMuted",
  }),

  // Virtual colors for semantic states
  error: virtualColor({
    name: "error",
    light: "lightError",
    dark: "darkError",
  }),
  textOnError: virtualColor({
    name: "textOnError",
    light: "lightTextOnError",
    dark: "darkTextOnError",
  }),
  warning: virtualColor({
    name: "warning",
    light: "lightWarning",
    dark: "darkWarning",
  }),
  info: virtualColor({
    name: "info",
    light: "lightInfo",
    dark: "darkInfo",
  }),
  primaryHighlight: virtualColor({
    name: "primaryHighlight",
    light: "lightPrimaryHighlight",
    dark: "darkPrimaryHighlight",
  }),

  // Cost change virtual colors
  costIncrease: virtualColor({
    name: "costIncrease",
    light: "lightCostIncrease",
    dark: "darkCostIncrease",
  }),
  costDecrease: virtualColor({
    name: "costDecrease",
    light: "lightCostDecrease",
    dark: "darkCostDecrease",
  }),

  // Success state
  success: virtualColor({
    name: "success",
    light: "lightSuccess",
    dark: "darkSuccess",
  }),
} as const

export type Color = keyof typeof colors
